/* eslint-disable react-refresh/only-export-components */
import React, { Suspense, lazy, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { ChakraProvider, Spinner, Center } from '@chakra-ui/react';
import { registerSW } from 'virtual:pwa-register';
import './index.css';
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { usePageTracking } from '@utils/hooks/usePageTracking';
import { initTracking, trackStartTrial } from '@utils/analytics';
import AdminRoute from '@blocks/AdminRoute';
import ProtectedRoute from '@blocks/ProtectedRoute';
import Layout from '@blocks/layouts/Layout';
import { Toaster } from '@components/ui/toaster';
import InstallBanner from '@blocks/InstallBanner';
import PaymentFailedBanner from '@blocks/PaymentFailedBanner';
import CookieConsentBanner from '@blocks/CookieConsentBanner';
// PRD 8: mandatory post-race screen, raised at app opening for a V2 athlete.
import PostRaceGate from '@blocks/postRace/PostRaceGate';
// PRD 12: the post-cutover settings confirmation, raised at app opening.
import MigrationGate from '@blocks/migration/MigrationGate';

import { MotionConfig } from 'motion/react';
import { AuthProvider } from '@context/AuthContext';
import { SyncProvider } from '@context/SyncContext';
import { PlanSelectionProvider } from '@context/PlanSelectionContext';
import queryClient from '@utils/queryClient';
import { system } from './theme/system';
import * as Sentry from "@sentry/react";

registerSW({
  immediate: true
});

// Lazy load auth pages
// const Landing = lazy(() => import('@views/Landing'));
const Login = lazy(() => import('@views/Login'));
const Signup = lazy(() => import('@views/Signup'));
const ForgotPassword = lazy(() => import('@views/ForgotPassword'));
const ResetPassword = lazy(() => import('@views/ResetPassword'));

// Lazy load main app pages
// D8: one route, two parcours — the switcher reads VITE_ONBOARDING_V2.
const Onboarding = lazy(() => import('@views/OnboardingRoute'));
const Home = lazy(() => import('@views/Home'));
// D7: one route, two worlds — the switcher picks on `plan_v2_id`.
const Workout = lazy(() => import('@views/WorkoutRoute'));
const Competition = lazy(() => import('@views/CompetitionV2'));
const Account = lazy(() => import('@views/Account'));
const MyTraining = lazy(() => import('@views/MyTraining'));
// PRD 8 §4: the objective flow re-entered outside onboarding.
const NewObjective = lazy(() => import('@views/NewObjective'));
const Advices = lazy(() => import('@views/Advices'));

// Lazy load admin pages
const CreatePlan = lazy(() => import('@blocks/Admin/plans/CreatePlan'));
const ListPlans = lazy(() => import('@blocks/Admin/plans/ListPlans'));
const PreviewPlan = lazy(() => import('@blocks/Admin/plans/PreviewPlan'));
const EditPlan = lazy(() => import('@blocks/Admin/plans/EditPlan'));

// Lazy load misc pages
const GarminConnection = lazy(() => import('@views/GarminConnection'));
const CorosConnection = lazy(() => import('@views/CorosConnection'));

// Loading fallback component
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color="blue.500" />
  </Center>
);

const PageTracker = () => {
  usePageTracking();
  return null;
};

const TrialTokenDetector = () => {
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const token = searchParams.get('op');
    if (token) {
      sessionStorage.setItem('trial_token', token);
    }
  }, [searchParams]);
  return null;
};

const TrialStartDetector = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('trial_started') === '1') {
      trackStartTrial();
      const next = new URLSearchParams(searchParams);
      next.delete('trial_started');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  return null;
};

let affonsoScriptLoaded = false;

const AffonsoPixelLoader = () => {
  useEffect(() => {
    if (affonsoScriptLoaded) {
      if (localStorage.getItem('cookie_consent') === 'accepted') {
        window.affonsoConsentGranted?.();
      }
      return;
    }
    affonsoScriptLoaded = true;

    if (localStorage.getItem('cookie_consent') === 'accepted') {
      window.affonsoConsentGranted?.();
    }

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = 'https://cdn.affonso.io/js/pixel.min.js';
    script.setAttribute('data-affonso', import.meta.env.VITE_AFFONSO_PROGRAM_ID ?? '');
    script.setAttribute('data-cookie_duration', '14');
    script.setAttribute('data-requires-consent', 'true');
    script.onload = () => {
      if (localStorage.getItem('cookie_consent') === 'accepted') {
        window.affonsoConsentGranted?.();
      }
    };
    document.head.appendChild(script);
  }, []);
  return null;
};

initTracking();

const env = import.meta.env.VITE_ENV;
const allowSentryTrack = ["development", "production"].includes(env) ?? false;
if (allowSentryTrack) {
  Sentry.init({
    environment: env,
    enabled: env !== 'local',
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [
      Sentry.replayIntegration(),
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [
      "localhost",
      import.meta.env.VITE_API_URL
    ],
  });
}


const RedirectKeepSearch = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

const AppShell = () => (
  <SyncProvider>
    <AuthProvider>
      <PageTracker />
      <TrialTokenDetector />
      <TrialStartDetector />
      <AffonsoPixelLoader />
      <InstallBanner />
      <PaymentFailedBanner />
      <CookieConsentBanner />
      <PostRaceGate />
      <MigrationGate />
      <Toaster />
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </AuthProvider>
  </SyncProvider>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppShell />}>
      {/* <Route path="/" element={<Landing />} /> */}
      <Route path="/signup" element={<Signup />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/forgot-password/:userToken" element={<ResetPassword />} />
      <Route path="/garmin-auth/" element={<GarminConnection />} />
      <Route path="/coros-auth/" element={<CorosConnection />} />
      <Route path="*" element={<Navigate to='/dashboard' />} />

      <Route element={<ProtectedRoute />}>
        <Route path="/onboarding" element={<PlanSelectionProvider><Onboarding /></PlanSelectionProvider>} />
        <Route element={<Layout />}>
          <Route path="/dashboard" element={<Home />} />
          <Route path="/my-progress" element={<MyTraining />} />
          <Route path="/advices" element={<Advices />} />
          <Route path="/workout/:uuid" element={<Workout />} />
          <Route path="/competition/:enrollmentId" element={<Competition />} />
          <Route path="/new-objective" element={<NewObjective />} />
        </Route>
        <Route element={<Layout maxWidth="full" />}>
          <Route path="/account" element={<Account />} />
          <Route path="/account/:section" element={<Account />} />
        </Route>
      </Route>

      {/* UPDATE-17: Profil, Paramètres and Parrainage merged into Mon compte */}
      <Route path="/profile" element={<RedirectKeepSearch to="/account" />} />
      <Route path="/settings" element={<RedirectKeepSearch to="/account" />} />
      <Route path="/referral" element={<RedirectKeepSearch to="/account/referral" />} />

      <Route path="/admin" element={<AdminRoute />}>
        <Route element={<Layout maxWidth="4xl" />}>
          <Route path="plans" element={<ListPlans />} />
        </Route>
        <Route element={<Layout maxWidth="none" />}>
          <Route path="plans/create" element={<CreatePlan />} />
          <Route path="plans/edit/:uuid" element={<EditPlan />} />
        </Route>
        <Route element={<Layout />}>
          <Route path="plans/view/:uuid" element={<PreviewPlan />} />
          <Route path="plans/view/:uuid/:week" element={<PreviewPlan />} />
        </Route>
      </Route>

      {/* Redirects for old admin URLs */}
      <Route path="/list-plans" element={<Navigate to="/admin/plans" replace />} />
      <Route path="/create-plan" element={<Navigate to="/admin/plans/create" replace />} />
      <Route path="/view-plan/:uuid" element={<Navigate to="/admin/plans/view/:uuid" replace />} />
      <Route path="/view-plan/:uuid/:week" element={<Navigate to="/admin/plans/view/:uuid/:week" replace />} />
    </Route>
  )
);

// App component with lazy Chakra system initialization
function App() {
  // Lazy initialize Chakra system to prevent initialization order issues
  // const system = useMemo(() => createSystem(defaultConfig, config), []);

  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider value={system}>
        <MotionConfig reducedMotion="user" transition={{ duration: 0.2 }}>
          <RouterProvider router={router} />
        </MotionConfig>
      </ChakraProvider>
    </QueryClientProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
